"use client";

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * A2 — keyboard shortcuts help overlay. Surfaces the hotkey legend
 * without requiring the presenter to open the chrome panel.
 *
 * Toggled by `?` (the universal "what can I do here?" key). Esc or
 * clicking the backdrop closes it.
 *
 * The legend lives here as the canonical reference; the chrome panel's
 * own HotkeysLegend strip is a compact mirror of the same content.
 */
const HOTKEYS: { combo: string; label: string }[] = [
  { combo: "?",         label: "Reveal / hide the presenter chrome panel" },
  { combo: "Cmd + ,",   label: "Toggle this help overlay" },
  { combo: "E",         label: "Force exporter perspective (Aurora / Gulf Phosphate)" },
  { combo: "I",         label: "Force importer perspective (Al Madar / Pampagro)" },
  { combo: "N",         label: "Advance to the next screen (manual-mode pacing)" },
  { combo: "Shift + N", label: "Rewind to the previous screen checkpoint" },
  { combo: "Shift + B", label: "Rewind to the most recent act boundary" },
  { combo: "D",         label: "Re-open the active scene's sign-document modal" },
  { combo: "J",         label: "Open the event-log inspector overlay" },
  { combo: "Esc",       label: "Dismiss the topmost overlay / panel" },
];

export function KeyboardHelpOverlay({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  // Esc closes the overlay even when the global hotkey handler is
  // suppressed (e.g. focus inside an input). The presenter doesn't
  // have to remember any nested escape semantics — Esc just closes.
  const t = useTranslations("keyboardHelpOverlay");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  // Portal: escape any transformed ancestor (.scene-fade) so the help
  // overlay centres on the viewport.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="keyboard-help-title"
      className="fixed inset-0 z-[60] grid place-items-center bg-orbitra-ink/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="glass relative w-[min(640px,90vw)] max-h-[80vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label={t("closeAriaLabel")}
          onClick={onClose}
          className="absolute end-3 top-2 text-[18px] leading-none text-orbitra-muted hover:text-orbitra-paper"
        >
          ×
        </button>

        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
          Presenter hotkeys
        </p>
        <h2
          id="keyboard-help-title"
          className="mt-1 font-display text-lg font-semibold text-orbitra-paper"
        >
          Keyboard shortcuts
        </h2>

        <ul className="mt-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {HOTKEYS.map((h) => (
            <li
              key={h.combo}
              className="flex items-baseline justify-between gap-3 rounded border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2"
            >
              <kbd className="rounded border border-state-verified/40 bg-state-verified/8 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-state-verified">
                {h.combo}
              </kbd>
              <span className="flex-1 text-end text-[11px] text-orbitra-paper">
                {h.label}
              </span>
            </li>
          ))}
        </ul>

        <p className="mt-4 font-mono text-[9px] text-orbitra-muted">
          Hotkeys are suppressed when focus is in an input or textarea.
          Press <kbd className="rounded border border-orbitra-muted/40 px-1 font-mono text-[9px] text-orbitra-paper">Esc</kbd> or click anywhere outside this panel to dismiss.
        </p>
      </div>
    </div>,
    document.body,
  );
}
